<template>
    <div class="common-layout layout-root">
        <div class="layout-padding-wrapper">
            <div class="header-sticky">
                <HeaderLogo :groupName="groupName" :currentUrl="currentUrl" />
                <HeaderBar
                    @save="handleCreate"
                    :vacancyData="vacancyData"
                    :consultantData="consultantData"
                    :profileId="profileId"
                    :senderId="senderId"
                    :senderEmail="senderEmail"
                    :hidePdfBtn="true"
                    :candidateData="candidateData"
                    :saveButtonText="
                        $trans('profile_dossier.create_new_dossier')
                    "
                />
            </div>
        </div>

        <div class="layout-main">
            <div class="layout-padding-wrapper">
                <div class="table-container">
                    <el-input
                        v-model="search"
                        :placeholder="
                            $trans('profile_dossier.search_placeholder')
                        "
                        clearable
                        class="mb-4"
                    >
                        <template #suffix>
                            <img
                                src="/media/images/search_gray.png"
                                alt="Search"
                                style="width: 16px; height: 16px; opacity: 0.6"
                            />
                        </template>
                    </el-input>

                    <EasyDataTable
                        :headers="headers"
                        :items="sortedItems"
                        :rows-per-page="5"
                        :search-value="search"
                        class="dataTable"
                        table-class-name="dossiers-table"
                        buttons-pagination
                    >
                        <template #header-save_info>
                            <div class="custom-table-header">
                                <span
                                    @click.stop="changeSort('save_date')"
                                    :class="{ active: sortBy === 'save_date' }"
                                >
                                    {{ $trans("profile_dossier.save_date") }}
                                    <SortIcons
                                        :columnKey="'save_date'"
                                        :sortBy="sortBy"
                                        :sortDesc="sortDesc"
                                    />
                                </span>
                                <span
                                    @click.stop="changeSort('type')"
                                    :class="{ active: sortBy === 'type' }"
                                >
                                    {{ $trans("profile_dossier.file_type") }}
                                    <SortIcons
                                        :columnKey="'type'"
                                        :sortBy="sortBy"
                                        :sortDesc="sortDesc"
                                    />
                                </span>
                            </div>
                        </template>

                        <template #header-file_info>
                            <div class="custom-table-header">
                                <span
                                    @click.stop="changeSort('vacancy_name')"
                                    :class="{
                                        active: sortBy === 'vacancy_name',
                                    }"
                                >
                                    {{ $trans("profile_dossier.vacancy_name") }}
                                    <SortIcons
                                        :columnKey="'vacancy_name'"
                                        :sortBy="sortBy"
                                        :sortDesc="sortDesc"
                                    />
                                </span>
                                <span
                                    @click.stop="changeSort('file_name')"
                                    :class="{ active: sortBy === 'file_name' }"
                                >
                                    {{ $trans("profile_dossier.file_name") }}

                                    <SortIcons
                                        :columnKey="'file_name'"
                                        :sortBy="sortBy"
                                        :sortDesc="sortDesc"
                                    />
                                </span>
                            </div>
                        </template>

                        <template #item-save_info="{ save_date, type }">
                            <div class="cell-multiline">
                                <div>{{ save_date }}</div>
                                <div>{{ type }}</div>
                            </div>
                        </template>
                        <template #item-file_info="{ vacancy_name, file_name }">
                            <div class="cell-multiline">
                                <div>{{ vacancy_name }}</div>
                                <div>{{ file_name }}</div>
                            </div>
                        </template>

                        <template #item-action="{ copy_url }">
                            <el-button
                                type="primary"
                                class="copy-dossier-btn"
                                @click="handleCopy(copy_url)"
                                :disabled="isCopying"
                            >
                                <DownloadIcon />
                                {{ $trans("profile_dossier.copy_dossier") }}
                            </el-button>
                        </template>
                    </EasyDataTable>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import HeaderLogo from "./Components/HeaderLogo";
import HeaderBar from "./Components/HeaderBar.vue";

import EasyDataTable from "vue3-easy-data-table";
import dayjs from "dayjs";
import SortIcons from "./Components/Tables/SortIcons.vue";

import { ElButton } from "element-plus";
import { Download as DownloadIcon } from "@element-plus/icons-vue";

export default {
    name: "List",
    components: {
        HeaderLogo,
        HeaderBar,
        EasyDataTable,
        ElButton,
        DownloadIcon,
        SortIcons,
    },
    props: {
        groupName: String,
        profileId: Number,
        senderId: Number,
        senderEmail: String,
        vacancyData: Array,
        consultantData: Array,
        candidateData: Array,
        dossierList: {
            type: Array,
            default: () => [],
        },
        create_url: String,
    },
    mounted() {
        this.$functions.hideLoader();
    },
    data() {
        return {
            search: "",
            sortBy: "save_date",
            sortDesc: true,
            isCopying: false,
            headers: [
                {
                    text: "Speicherdatum / Datei-Typ",
                    value: "save_info",
                    sortable: true,
                },
                {
                    text: "Vakanz-Name / Dateiname",
                    value: "file_info",
                    sortable: true,
                },
                {
                    text: "Action",
                    value: "action",
                    width: 200,
                },
            ],
        };
    },
    computed: {
        sortedItems() {
            if (!this.sortBy) return this.dossierList;

            let items = [...this.dossierList];

            items.sort((a, b) => {
                let valA = a[this.sortBy];
                let valB = b[this.sortBy];

                if (this.sortBy === "save_date") {
                    valA = new Date(valA);
                    valB = new Date(valB);
                }

                if (valA == null) return 1;
                if (valB == null) return -1;

                if (valA < valB) return this.sortDesc ? 1 : -1;
                if (valA > valB) return this.sortDesc ? -1 : 1;
                return 0;
            });

            return items;
        },
    },
    methods: {
        handleCreate() {
            const form = document.createElement("form");
            form.method = "POST";
            form.action = this.create_url;
            const csrfInput = document.createElement("input");
            csrfInput.type = "hidden";
            csrfInput.name = "_token";
            csrfInput.value = document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute("content");
            form.appendChild(csrfInput);

            document.body.appendChild(form);
            form.submit();
        },
        handleCopy(url) {
            if (this.isCopying) return;
            
            this.isCopying = true;
            window.location.href = url;
        
            setTimeout(() => {
                this.isCopying = false;
            }, 3000);
        },
        formatDate(date) {
            return dayjs(date).format("DD.MM.YYYY");
        },
        changeSort(column) {
            if (this.sortBy === column) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = column;
                this.sortDesc = false;
            }
        },
    },
};
</script>

<style scoped>
.layout-root {
    height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: "Barlow", sans-serif;
}

.layout-padding-wrapper,
.header-sticky-wrapper {
    padding-left: 2rem;
    padding-right: 2rem;
    display: flex;
    justify-content: center;
}

.layout-padding-wrapper {
    background-color: #fff;
}

.header-sticky-wrapper {
    background-color: white;
}

.header-sticky {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    width: 100%;
    max-width: 95rem;
}

.layout-main {
    flex: 1;
    background-color: #fff;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.table-container {
    width: 100%;
    max-width: 95rem;
    box-sizing: border-box;
    padding-left: 2rem;
    padding-right: 2rem;
    padding-bottom: 24px;
    padding-top: 24px;
    background-color: #edededed;
    margin-top: 25px;
}

.dossiers-table {
    width: 100%;
}

/* DTABLE TABLE */
:deep(.vue3-easy-data-table) {
    margin-bottom: unset !important;
}

:deep(.vue3-easy-data-table tr td) {
    height: 1px;
}

:deep(.vue3-easy-data-table tr th),
:deep(.vue3-easy-data-table tr td) {
    border: 2px solid #ededed;
}

:deep(.vue3-easy-data-table tr td) {
    background: #ffffff;
    padding: 0.5rem;
    line-height: 20px;
}

:deep(.vue3-easy-data-table tr th) {
    border-top: 0;
    border-bottom: 0;
    background: rgb(237, 237, 237);
    padding: 0 0.5rem;
    color: #3c3c3c;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    height: 24px;
    line-height: 24px;
    font-family: sans-serif;
    border-top: unset;
}

:deep(.vue3-easy-data-table__body tr:hover td:last-child) {
    background-color: #d2e1e5 !important;
}

:deep(.vue3-easy-data-table) {
    border: none !important;
}

:deep(.vue3-easy-data-table__footer) {
    background-color: transparent !important;
}

:deep(.vue3-easy-data-table__body tr td:last-child) {
    background-color: #d2e1e5;
}

:deep(.pagination__items-index) {
    display: none !important;
}

:deep(.el-input__prefix) {
    width: 0 !important;
}

:deep(.el-input) {
    width: 25%;
}

:deep(.el-input__wrapper) {
    border-radius: 0 !important;
    margin-bottom: 20px;
}

:deep(.buttons-pagination .item.button.active) {
    color: #fff;
    background: linear-gradient(
        90deg,
        rgb(65, 171, 196) 0%,
        rgb(119, 214, 241) 100%
    );
    border-color: rgb(65, 171, 196);
    border-radius: 0;
}

:deep(.buttons-pagination .item.button) {
    border-radius: 0;
}

:deep(.buttons-pagination) {
    --40dd4f07: unset !important;
    transform: scale(1.5);
    margin-top: 25px;
}

:deep(.vue3-easy-data-table__main) {
    background-color: #ededed;
}

:deep(.next-page__click-button .arrow) {
    margin-top: 21px;
    margin-left: 40px;
}

:deep(.previous-page__click-button .arrow) {
    margin-right: 40px;
    margin-top: 21px;
}

.searcher {
    height: 40px;
    width: 295px;
    font-weight: 600;
}

:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
}

:deep(.pagination__rows-per-page) {
    display: none !important;
}

:deep(.vue3-easy-data-table__footer) {
    justify-content: center !important;
}

.dossiers-table {
    --easy-table-header-font-size: 14px;
    --easy-table-body-row-font-size: 14px;

    --easy-table-body-row-hover-background-color: #fff;
}

.copy-dossier-btn {
    background-color: #41abc4;
    color: white;
    padding: 8px 38px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    border-radius: 0;
}

.copy-dossier-btn:hover {
    background-color: #2699b2;
}

:deep(.sortType-icon) {
    display: none !important;
}

.cell-multiline > div {
    line-height: 1.2;
}

.cell-multiline > div {
    line-height: 1.2;
}

.cell-multiline {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.custom-table-header {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    user-select: none;
    text-align: left;
}

:deep(.sortType-icon) {
    display: none !important;
}

.custom-table-header {
  display: flex !important;
  justify-content: space-between !important;
  width: 100% !important;
}

.dossiers-table th {
  position: relative !important;
}
</style>
